/**
 * This is a wrapper component for different Layouts.
 * Navigational 'aside' content should be added to this wrapper.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { FormattedMessage } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';

import { TabNav } from '../../../components';
import { isCompanyUserType, isPrivateUserType } from '../../../util/userTypes';
import { ensureCurrentUser } from '../../../util/data';

import { createGlobalState } from './hookGlobalState';

import css from './LayoutSideNavigation.module.css';

const { node, string } = PropTypes;

const MAX_HORIZONTAL_NAV_SCREEN_WIDTH = 1023;

// Add global state for tab scrolling effect
const initialScrollState = { scrollLeft: 0 };
const { useGlobalState } = createGlobalState(initialScrollState);

// Horizontal scroll animation using element.scrollTo()
const scrollToTab = (currentPage, scrollLeft, setScrollLeft) => {
  const el = document.querySelector(`#${currentPage}Tab`);

  // if el is null return
  if (!el) {
    return;
  }

  if (el) {
    // el.scrollIntoView doesn't work with Safari and it considers vertical positioning too.
    // This scroll behaviour affects horizontal scrolling only
    // and it expects that the immediate parent element is scrollable.
    const parent = el.parentElement;
    const parentRect = parent.getBoundingClientRect();
    const maxScrollDistance = parent.scrollWidth - parentRect.width;

    const hasParentScrolled = parent.scrollLeft > 0;
    const scrollPositionCurrent = hasParentScrolled ? parent.scrollLeft : scrollLeft;

    const tabRect = el.getBoundingClientRect();
    const diffLeftBetweenTabAndParent = tabRect.left - parentRect.left;
    const tabScrollPosition = parent.scrollLeft + diffLeftBetweenTabAndParent;

    const scrollPositionNew =
      tabScrollPosition > maxScrollDistance
        ? maxScrollDistance
        : parent.scrollLeft + diffLeftBetweenTabAndParent;

    const needsSmoothScroll = scrollPositionCurrent !== scrollPositionNew;

    if (parent.scrollTo && (!hasParentScrolled || (hasParentScrolled && needsSmoothScroll))) {
      // Ensure that smooth scroll animation uses old position as starting point after navigation.
      parent.scrollTo({ left: scrollPositionCurrent });
      // Scroll to new position
      parent.scrollTo({ left: scrollPositionNew, behavior: 'smooth' });
    }
    // Always keep track of new position (even if smooth scrolling is not applied)
    setScrollLeft(scrollPositionNew);
  }
};

const LayoutWrapperAccountSettingsSideNavComponent = props => {
  const [mounted, setMounted] = useState(false);
  const [scrollLeft, setScrollLeft] = useGlobalState('scrollLeft');

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) {
      const { currentPage } = props;
      const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
      const hasHorizontalTabLayout = hasMatchMedia
        ? window.matchMedia(`(max-width: ${MAX_HORIZONTAL_NAV_SCREEN_WIDTH}px)`)?.matches
        : true;

      // Check if scrollToTab call is needed (tab is not visible on mobile)
      if (hasHorizontalTabLayout) {
        scrollToTab(currentPage, scrollLeft, setScrollLeft);
      }
    }
  }, [mounted]);

  const { currentPage, currentUser } = props;

  // Get user type to determine which tabs to show
  const user = ensureCurrentUser(currentUser);
  const userType = user.attributes?.profile?.publicData?.userType;
  const isPrivateUser = isPrivateUserType(userType);
  const isBroker = isCompanyUserType(userType);

  const tabs = [
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.contactDetailsTabTitle" />,
      selected: currentPage === 'ContactDetailsPage',
      id: 'ContactDetailsPageTab',
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.passwordTabTitle" />,
      selected: currentPage === 'PasswordChangePage',
      id: 'PasswordChangePageTab',
      linkProps: {
        name: 'PasswordChangePage',
      },
    },
    // Payout details are only relevant for broker/company users.
    ...(isBroker ? [{
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentsTabTitle" />,
      selected: currentPage === 'StripePayoutPage',
      id: 'StripePayoutPageTab',
      linkProps: {
        name: 'StripePayoutPage',
      },
    }] : []),
    // Payment Methods - only for brokers/service providers
    ...(isBroker ? [{
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentMethodsTabTitle" />,
      selected: currentPage === 'PaymentMethodsPage',
      id: 'PaymentMethodsPageTab',
      linkProps: {
        name: 'PaymentMethodsPage',
      },
    }] : []),
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.notificationsTabTitle" />,
      selected: currentPage === 'NotificationPreferencesPage',
      id: 'NotificationPreferencesPageTab',
      linkProps: {
        name: 'NotificationPreferencesPage',
      },
    },
    // Company Notifications - only for brokers/companies
    ...(isBroker ? [{
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.companyNotificationsTabTitle" />,
      selected: currentPage === 'CompanyNotificationPreferencesPage',
      id: 'CompanyNotificationPreferencesPageTab',
      linkProps: {
        name: 'CompanyNotificationPreferencesPage',
      },
    }] : []),
    // Subscriptions - temporarily hidden; keep implementation for quick rollback
    ...(false && isBroker ? [{
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.subscriptionsTabTitle" />,
      selected: currentPage === 'Account',
      id: 'SubscriptionsPageTab',
      linkProps: {
        name: 'SubscriptionsPage',
      },
    }] : []),
    // Purchase Credits - only for brokers/service providers
    ...(isBroker ? [{
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.creditsPurchaseTitle" />,
      selected: currentPage === 'Account',
      id: 'PurchaseCreditsPageTab',
      linkProps: {
        name: 'CreditsPurchasePage',
      },
    }] : []),
  ];

  return <TabNav rootClassName={css.tabs} tabRootClassName={css.tab} tabs={tabs} />;


};

LayoutWrapperAccountSettingsSideNavComponent.defaultProps = {
  className: null,
  rootClassName: null,
  children: null,
  currentPage: null,
  currentUser: null,
};

LayoutWrapperAccountSettingsSideNavComponent.propTypes = {
  children: node,
  className: string,
  rootClassName: string,
  currentPage: string,
  currentUser: propTypes.currentUser,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return {
    currentUser,
  };
};

const LayoutWrapperAccountSettingsSideNav = connect(mapStateToProps)(
  LayoutWrapperAccountSettingsSideNavComponent
);

export default LayoutWrapperAccountSettingsSideNav;
